import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import moment from "moment";
import type { IntervalRange } from "src/components/ComparePeriodSelector/ComparePeriodSelector";
import { useTranslation } from "src/design-system/core";

import useAuth from "src/hooks/useAuth";
import type { CompareMethodKeyEnum } from "src/types/enum/CompareMethodKey";
import type { Id } from "src/types/id";
import type {
    EnhancedOrganization,
    Organization,
    OrganizationDetails,
} from "src/types/organization";
import { Query } from "src/types/query";
import { UserRoleEnum } from "src/types/role";
import axios from "src/utils/axios";
import { FORMAT_DATE_STD } from "src/utils/formatters";
import { filterOrganizationsToDisplay } from "src/utils/shouldDisplayEntity";
import type { EvolutionStepEnum } from "src/views-ds/dashboard/dashboard-view/DashboardUtils/Dashboard.utils";

export interface QueryOrganizationsProps {
    countPosAndUser?: boolean;
    light?: boolean;
}

export const useQueryOrganizations = (
    params: QueryOrganizationsProps = {},
    enabled = true
): UseQueryResult<OrganizationDetails[], unknown> => {
    const { user } = useAuth();
    const displayHiddenEntities = user?.role === UserRoleEnum.SUPERADMIN;

    return useQuery({
        queryKey: [Query.Organizations, user.id, params.light, params.countPosAndUser],

        queryFn: async () =>
            filterOrganizationsToDisplay(
                (
                    await axios.get<{ organizations: OrganizationDetails[] }>(`/organization`, {
                        params,
                    })
                ).data.organizations,
                displayHiddenEntities
            ),

        enabled,
        placeholderData: keepPreviousData,
    });
};

export function useQueryOrganizationsWithSfInfos(
    enabled = true
): UseQueryResult<EnhancedOrganization[], unknown> {
    const { user } = useAuth();

    return useQuery({
        queryKey: [Query.OrganizationsWithSfInfos, user.id],

        queryFn: async () =>
            (
                await axios.get<{ organizations: EnhancedOrganization[] }>(
                    `/organization/with-sf-info`
                )
            ).data.organizations,

        enabled,
        placeholderData: keepPreviousData,
    });
}

export const useQueryOrganization = (
    organizationId: Id,
    keepPreviousDataBoolean = false
): UseQueryResult<Organization, unknown> => {
    const { user } = useAuth();

    return useQuery({
        queryKey: [Query.Organization, user.id, organizationId],

        queryFn: async () =>
            (await axios.get<{ organization: Organization }>(`/organization/${organizationId}`))
                .data.organization,

        placeholderData: keepPreviousDataBoolean ? keepPreviousData : undefined,
    });
};

export const useQueryOrganizationDetails = (
    organizationId: Id,
    keepPreviousDataBoolean = false,
    relations = ["poss"],
    countPosAndUser?: boolean,
    fetchFeatureFlags?: boolean
): UseQueryResult<OrganizationDetails> => {
    return useQuery({
        queryKey: [
            Query.Organization,
            organizationId,
            relations.join(","),
            countPosAndUser,
            fetchFeatureFlags,
        ],

        queryFn: async () =>
            (
                await axios.get<{ organization: OrganizationDetails }>(
                    `/organization/${organizationId}/details`,
                    {
                        params: { relations, countPosAndUser, fetchFeatureFlags },
                    }
                )
            ).data.organization,

        placeholderData: keepPreviousDataBoolean ? keepPreviousData : undefined,
    });
};

export interface UseQueryOrganizationEvolutionResponse {
    base: (OrganizationEvolutionMetric & { date: Date })[];
    compare: (OrganizationEvolutionMetric & { dateList: Date[] })[];
}

export interface OrganizationEvolutionMetric {
    sumProfit: number;
    sumInclVat: number;
    countTicket: number;
    meanBasket: number;
}

export const useQueryOrganizationEvolution = (
    {
        channelIds,
        baseIntervalRanges,
        compareIntervalRanges,
        posIds,
        evolutionStep,
        fullDataComparePeriod,
        compareMethodKey,
    }: {
        posIds?: number[];
        channelIds?: number[];
        baseIntervalRanges: IntervalRange[];
        compareIntervalRanges: IntervalRange[];
        evolutionStep: EvolutionStepEnum;
        fullDataComparePeriod: boolean;
        compareMethodKey: CompareMethodKeyEnum;
    },
    { enabled }: { enabled?: boolean } = { enabled: true }
): UseQueryResult<UseQueryOrganizationEvolutionResponse> => {
    const { user } = useAuth();
    const { translateCommon } = useTranslation();

    return useQuery({
        queryKey: [
            Query.OrganizationEvolution,
            user.organization.id,
            JSON.stringify(baseIntervalRanges),
            JSON.stringify(compareIntervalRanges),
            JSON.stringify(posIds),
            JSON.stringify(channelIds),
            compareMethodKey,
            evolutionStep,
            fullDataComparePeriod,
        ],

        queryFn: async () => {
            const response = await axios.post(
                `/organization/${user.organization.id}/sales/evolution`,
                {
                    baseFroms: baseIntervalRanges.map((intervalRange) =>
                        moment(intervalRange.startDate).format(FORMAT_DATE_STD)
                    ),
                    baseTos: baseIntervalRanges.map((intervalRange) =>
                        moment(intervalRange.endDate).format(FORMAT_DATE_STD)
                    ),
                    compareFroms: compareIntervalRanges.map((intervalRange) =>
                        moment(intervalRange.startDate).format(FORMAT_DATE_STD)
                    ),
                    compareTos: compareIntervalRanges.map((intervalRange) =>
                        moment(intervalRange.endDate).format(FORMAT_DATE_STD)
                    ),
                    channelIds,
                    posIds: posIds.length ? posIds : undefined,
                    evolutionStep,
                    compareMethodKey,
                    fullDataComparePeriod,
                }
            );
            return response.data;
        },

        meta: {
            errorMessage: translateCommon("errors.unknown"),
        },
        enabled,
        placeholderData: keepPreviousData,
    });
};

export const useQueryOrganizationEvolutionTrend = (
    {
        channelIds,
        baseIntervalRanges,
        compareIntervalRanges,
        evolutionStep,
        fullDataComparePeriod,
    }: {
        channelIds?: number[];
        baseIntervalRanges: IntervalRange[];
        compareIntervalRanges: IntervalRange[];
        evolutionStep: EvolutionStepEnum;
        fullDataComparePeriod: boolean;
    },
    { enabled }: { enabled?: boolean } = { enabled: true }
): UseQueryResult<UseQueryOrganizationEvolutionResponse> => {
    const { user } = useAuth();
    const { translateCommon } = useTranslation();

    return useQuery({
        queryKey: [
            Query.OrganizationEvolutionTrend,
            user.organization.id,
            JSON.stringify(baseIntervalRanges),
            JSON.stringify(compareIntervalRanges),
            JSON.stringify(channelIds),
            fullDataComparePeriod,
        ],

        queryFn: async () => {
            const response = await axios.post(
                `/organization/${user.organization.id}/sales/evolution-trend`,
                {
                    baseFroms: baseIntervalRanges.map((intervalRange) =>
                        moment(intervalRange.startDate).format(FORMAT_DATE_STD)
                    ),
                    baseTos: baseIntervalRanges.map((intervalRange) =>
                        moment(intervalRange.endDate).format(FORMAT_DATE_STD)
                    ),
                    compareFroms: compareIntervalRanges.map((intervalRange) =>
                        moment(intervalRange.startDate).format(FORMAT_DATE_STD)
                    ),
                    compareTos: compareIntervalRanges.map((intervalRange) =>
                        moment(intervalRange.endDate).format(FORMAT_DATE_STD)
                    ),
                    channelIds,
                    evolutionStep,
                    fullDataComparePeriod,
                }
            );
            return response.data;
        },

        meta: {
            errorMessage: translateCommon("errors.unknown"),
        },
        enabled,
        placeholderData: keepPreviousData,
    });
};

export function useQueryOpeningHoursByOrgaId({
    posIdList,
}: {
    posIdList?: number[];
}): UseQueryResult<any> {
    const { user } = useAuth();

    return useQuery({
        queryKey: [Query.OrganizationOpeningHours, user.organization.id, JSON.stringify(posIdList)],
        queryFn: async () => {
            const response = await axios.post(
                `/organization/${user.organization.id}/opening-hours/list`,
                {
                    posIdList,
                }
            );

            return response.data.openingHours;
        },
    });
}
